/**
 * PLAT-11213 — what a generated reel name has to be before it becomes a clip
 * title, and what the run tells the model not to repeat.
 *
 * `Reel Util | Name a Reel` already forbids colons, quotation marks and
 * over-length names. It was observed doing all three anyway
 * (`"Before and After AI: Transformative Impacts on Business and Life"`), and
 * unlike a Section name — which lives in `clipJson` and is only ever read in the
 * Reel Editor — this one is written to the clip record and shows up in the clip
 * list, the preview header and every share and publish surface. So the prompt
 * states the rule and this enforces it.
 *
 * Pure — no React, no MobX, no store access.
 */

import type { Reel } from './reelTypes';

/** Straight and curly pairs, plus guillemets — whatever the model wrapped it in. */
const QUOTE_PAIRS: ReadonlyArray<[string, string]> = [
  ['"', '"'],
  ["'", "'"],
  ['“', '”'],
  ['‘', '’'],
  ['«', '»'],
];

/** Sentence-ending and dangling punctuation a name should not carry. */
const TRAILING_PUNCTUATION = /[\s.,;:!\-–—]+$/;

/**
 * The model's raw answer, made into a name.
 *
 * Returns `''` when nothing usable is left, which the caller treats the same as
 * no answer at all: the title stands and the run is reported as a failure.
 */
export function normalizeGeneratedReelName(raw: string): string {
  let name = raw.trim();

  // Twice, because a model that quotes will sometimes quote what it already
  // quoted. Bounded rather than looped — this is a stray character, not a
  // structure to parse.
  for (let pass = 0; pass < 2; pass += 1) {
    name = stripWrappingQuotes(name);
  }

  // "Label: statement" is the failure mode the prompt names, and the label half
  // is the name — the tail is a subtitle the clip list has no room for, and it
  // is where the over-long names come from. Falls back to the tail only when
  // there is no head at all (a leading colon).
  const colon = name.indexOf(':');
  if (colon !== -1) {
    const head = name.slice(0, colon).trim();
    name = head || name.slice(colon + 1).trim();
  }

  name = name.replace(TRAILING_PUNCTUATION, '').trim();
  // A model that wrapped *and* punctuated leaves a quote behind the full stop.
  name = stripWrappingQuotes(name).trim();

  return name.replace(/\s+/g, ' ');
}

function stripWrappingQuotes(value: string): string {
  for (const [open, close] of QUOTE_PAIRS) {
    if (value.length > 1 && value.startsWith(open) && value.endsWith(close)) {
      return value.slice(open.length, -close.length).trim();
    }
  }
  return value;
}

/**
 * The names already in use inside this reel — its Sections' and its chapters'.
 *
 * They already reach the model through `chapterizationBaseText`, but as
 * *headings*: they say what the reel is made of, not what the whole thing may
 * not be called. Without them said as a constraint the model reuses one — a reel
 * whose second Section is "AI's Transformative Impact…" came back named
 * "…Transformative Impacts on Business and Life", which puts a part and the
 * whole under one name in the same clip list.
 *
 * Deduplicated case-insensitively, in reel order, first spelling kept.
 */
export function reelNamesInUse(reel: Reel | undefined): string[] {
  const seen = new Set<string>();
  const names: string[] = [];

  const add = (value: string | undefined) => {
    const name = value?.trim();
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(name);
  };

  for (const chapter of reel?.chapters ?? []) add(chapter.title);
  for (const section of Object.values(reel?.sections ?? {})) add(section?.name);
  for (const disabled of reel?.disabledSections ?? []) add(disabled.name);

  return names;
}

/**
 * The steer that carries them, or `undefined` when the reel has no names yet.
 *
 * Goes in as the run's `instructions` — the same channel the creator's own steer
 * uses, which lands as a line under the passage. The prompt record keeps the
 * voice and the shape of the label; this only says what the answer may not be.
 */
export function reelNameAvoidanceInstruction(
  namesInUse: readonly string[],
): string | undefined {
  if (!namesInUse.length) return undefined;
  return `These names are already used by parts of this reel: ${namesInUse
    .map(name => `"${name}"`)
    .join(
      ', ',
    )}. Name the reel as a whole, in your own words — do not return any of those names, and do not return a variation of one.`;
}
